import { useEffect, useState, useMemo, useRef, useCallback } from 'react';

import { usePersonalisationContext } from '../../context/PersonalisationContext';
import { shouldEnableAutoScroll, scrollToNextSlot } from '../../utilities/autoScrollToSlots';

/**
 * Custom hook for managing the personalisation step view model.
 * @param {Object} value - The current value object containing personalisation data.
 * @returns {Object} View model for the step component.
 */
export function useViewModel(value) {
  const {
    personalisationSlots,
    componentGroups,
    currentStepId,
    personalisationSteps,
    selections,
    updateSelections,
    hasPersonalisationOptions
  } = usePersonalisationContext();

  const [slots, setSlots] = useState(/** @type {import('../../gateway/personalisationData').Slot[]} */ ([]));
  const [step, setStep] = useState(/** @type {import('../../gateway/personalisationData').Step | null} */ (null));

  // Check if any slot has STAR_MAP type and set permanently once true.
  const [shouldShowStarMapSlot, setShouldShowStarMapSlot] = useState(false);

  // Auto-scroll functionality
  const slotRefs = useRef(new Map());
  const prevSelectionsRef = useRef(selections.slots);

  // Check if auto-scroll should be enabled
  const isAutoScrollEnabled = shouldEnableAutoScroll({
    slots,
    slotMode: step?.slotMode || 'FIXED',
    showTabs: step?.showTabs || false
  });

  useEffect(() => {
    // Get the step object for the current step from the Map.
    const theStep = personalisationSteps?.get(currentStepId);

    if (theStep) {
      // Get the slot objects for the step.
      const currentSlots =
        theStep.slots
          ?.map((slotID) => personalisationSlots?.get(slotID))
          .filter((slot) => typeof slot !== 'undefined') || [];
      // Sort slots by orderIndex to ensure proper ordering.
      const sortedSlots = currentSlots.sort((slotA, slotB) => {
        const orderIndexA = parseInt(slotA.orderIndex) || 0;
        const orderIndexB = parseInt(slotB.orderIndex) || 0;
        return orderIndexA - orderIndexB;
      });
      setSlots(Array.from(sortedSlots));
      setStep(theStep);
    }
  }, [currentStepId, personalisationSteps, personalisationSlots]);

  useEffect(() => {
    // Once shouldShowStarMapSlot is true, it stays true permanently.
    if (!shouldShowStarMapSlot && slots.some((slot) => slot.slotType === 'STAR_MAP')) {
      setShouldShowStarMapSlot(true);
    }
  }, [slots, shouldShowStarMapSlot]);

  // Watch for selection changes and trigger auto-scroll
  useEffect(() => {
    if (!isAutoScrollEnabled) return;

    const prevSelections = prevSelectionsRef.current;
    const currentSelections = selections.slots;

    // Find slots that have new selections
    currentSelections.forEach((currentSlot) => {
      const prevSlot = prevSelections.find((s) => s.id === currentSlot.id);

      // If this slot has a new selection (was null/undefined, now has a value)
      if (!prevSlot?.selected && currentSlot.selected) {
        scrollToNextSlot({
          slots,
          selections: currentSelections,
          currentSlotId: currentSlot.id,
          slotRefs: slotRefs.current
        });
      }
    });

    // Update the ref for next comparison
    prevSelectionsRef.current = currentSelections;
  }, [selections.slots, isAutoScrollEnabled, slots]);

  // Parse allowedFonts from string to array.
  const parsedAllowedFonts = useMemo(() => {
    // Early return if no value or allowedFonts.
    if (!value?.allowedFonts) return [];

    if (Array.isArray(value.allowedFonts)) {
      return value.allowedFonts;
    }

    if (typeof value.allowedFonts === 'string') {
      try {
        return JSON.parse(value.allowedFonts);
      } catch (e) {
        console.error('Failed to parse allowedFonts:', e);
        return [];
      }
    }

    return [];
  }, [value?.allowedFonts]);

  // Create ref callback for slot elements
  const createSlotRef = useCallback(
    (slotId) => (element) => {
      if (element) {
        slotRefs.current.set(slotId, element);
      } else {
        slotRefs.current.delete(slotId);
      }
    },
    []
  );

  // Disable slots if the maximum number of slots have been selected.
  const disabledSlots = useMemo(() => {
    if (step?.maxSlotsSelectable && step?.maxSlotsSelectable > 0) {
      const maxSlotsSelectable = step.maxSlotsSelectable;
      const selectedSlots = selections.slots
        .filter(({ stepId }) => stepId === step.id)
        .filter(({ selected }) => selected);

      if (selectedSlots.length >= maxSlotsSelectable) {
        return slots.map((slot) => {
          if (slot.stepId !== step.id) return slot;
          return {
            ...slot,
            disabled: !selectedSlots.some((selectedSlot) => selectedSlot.id === slot.id)
          };
        });
      } else {
        return slots.map((slot) => {
          if (slot.stepId !== step.id) return slot;
          return { ...slot, disabled: false };
        });
      }
    }
    return slots;
  }, [step?.maxSlotsSelectable, selections, slots]);

  return {
    slotMode: step?.slotMode,
    showTabs: step?.showTabs,
    slots: disabledSlots,
    componentGroups,
    selections,
    updateSelections,
    summaryLabel: step?.summaryLabel,
    parsedAllowedFonts,
    allowedFonts: value?.allowedFonts,
    hasPersonalisationOptions,
    createSlotRef,
    shouldEnableAutoScroll: isAutoScrollEnabled,
    step,
    shouldShowStarMapSlot
  };
}
